import React from 'react';
import { Star, Quote, Users, Award, Globe, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const WhyUs = () => {
  const { t } = useLanguage();

  const testimonials = [
    {
      name: t('testimonial-1-name'),
      company: t('testimonial-1-company'),
      role: t('testimonial-1-role'),
      content: t('testimonial-1-content'),
      rating: 5
    },
    {
      name: t('testimonial-2-name'),
      company: t('testimonial-2-company'),
      role: t('testimonial-2-role'),
      content: t('testimonial-2-content'),
      rating: 5
    },
    {
      name: t('testimonial-3-name'),
      company: t('testimonial-3-company'),
      role: t('testimonial-3-role'),
      content: t('testimonial-3-content'),
      rating: 5
    }
  ];

  const standards = [
    {
      icon: Award,
      title: t('standard-1-title'),
      description: t('standard-1-desc'),
      status: t('standard-1-status')
    },
    {
      icon: Shield,
      title: t('standard-2-title'),
      description: t('standard-2-desc'),
      status: t('standard-2-status')
    },
    {
      icon: Globe,
      title: t('standard-3-title'),
      description: t('standard-3-desc'),
      status: t('standard-3-status')
    }
  ];

  return (
    <section id="why-us" className="section-padding bg-neutral-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-800 mb-6">
            {t('why-us-title')}
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {t('why-us-subtitle')}
          </p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
          <div className="text-center bg-white p-6 rounded-2xl shadow-lg">
            <Users className="h-12 w-12 text-primary-500 mx-auto mb-4" />
            <div className="text-3xl font-bold text-primary-600 mb-2">1000+</div>
            <div className="text-neutral-600">{t('why-us-stats-customers')}</div>
          </div>
          <div className="text-center bg-white p-6 rounded-2xl shadow-lg">
            <Award className="h-12 w-12 text-primary-500 mx-auto mb-4" />
            <div className="text-3xl font-bold text-primary-600 mb-2">1500+</div>
            <div className="text-neutral-600">{t('why-us-stats-certificates')}</div>
          </div>
          <div className="text-center bg-white p-6 rounded-2xl shadow-lg">
            <Globe className="h-12 w-12 text-primary-500 mx-auto mb-4" />
            <div className="text-3xl font-bold text-primary-600 mb-2">25+</div>
            <div className="text-neutral-600">{t('why-us-stats-countries')}</div>
          </div>
          <div className="text-center bg-white p-6 rounded-2xl shadow-lg">
            <Star className="h-12 w-12 text-primary-500 mx-auto mb-4" />
            <div className="text-3xl font-bold text-primary-600 mb-2">4.9/5</div>
            <div className="text-neutral-600">{t('why-us-stats-rating')}</div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-neutral-800 mb-8 text-center">
            {t('why-us-testimonials-title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <Quote className="h-8 w-8 text-primary-200 mb-4" />
                <p className="text-neutral-600 mb-4 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-semibold text-neutral-800">{testimonial.name}</div>
                  <div className="text-sm text-neutral-600">{testimonial.role}, {testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Global Standards */}
        <div className="bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-semibold text-neutral-800 mb-8 text-center">
            {t('why-us-standards-title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {standards.map((standard, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-100 p-4 rounded-xl inline-block mb-4">
                  <standard.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h4 className="text-xl font-semibold text-neutral-800 mb-2">{standard.title}</h4>
                <p className="text-neutral-600 mb-3">{standard.description}</p>
                <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  {standard.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Blockchain Transparency */}
        <div className="mt-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 text-white">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-semibold mb-4">{t('why-us-blockchain-title')}</h3>
              <p className="text-primary-100 mb-6 leading-relaxed">
                {t('why-us-blockchain-desc')}
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>{t('why-us-blockchain-feature-1')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>{t('why-us-blockchain-feature-2')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>{t('why-us-blockchain-feature-3')}</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-4">{t('why-us-recent-transactions')}</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">CO2N-2024-001</span>
                  <span className="text-sm text-green-300">2.5 ton CO₂</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">CO2N-2024-002</span>
                  <span className="text-sm text-green-300">1.8 ton CO₂</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">CO2N-2024-003</span>
                  <span className="text-sm text-green-300">3.2 ton CO₂</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUs; 